import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';

import { FiActivity, FiBarChart2 } from 'react-icons/fi';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Filter, FilterContent, FilterRow, FilterSelect } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { DashboardContent } from '@/layouts/dashboard';
import { useSession } from '@/hooks/useSession';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { isTeacherOnly } from '@/lib/teacher-profile';
import { PENSANDO } from '@/pages/dashboard/home-teacher/teacher-character';

import TrilhaPerformanceModal from './TrilhaPerformanceModal';

const STATUS_CONFIG = {
  active: { label: 'Em andamento', variant: 'warning' },
  paused: { label: 'Pausada', variant: 'neutral-lightest' },
  completed: { label: 'Concluída', variant: 'success' }
};

// Tema playful (professor): mesmo status com badge "soft" (fundo claro + texto colorido).
const PLAYFUL_STATUS_VARIANT = {
  active: 'warning-soft',
  paused: 'muted',
  completed: 'success-soft'
};

const BLOCK_STATE = {
  done: { label: 'Concluído', variant: 'success' },
  skipped: { label: 'Pulado', variant: 'warning' },
  pending: { label: 'Pendente', variant: 'neutral-lightest' }
};

const BLOCK_KIND = {
  content: 'Conteúdo',
  review: 'Revisão'
};

// Mock: uma aula pode cobrir mais de uma área do conhecimento (Video HABTM Taxonomy no backend),
// mas o endpoint de drill-down hoje só retorna uma taxonomia por bloco. Até isso mudar no backend,
// simulamos uma segunda taxonomia em parte dos blocos para já cobrir o caso de até 2 tags na UI.
const MOCK_SECONDARY_TAXONOMIES = ['Interpretação de Texto', 'Raciocínio Lógico', 'Produção Textual', 'Resolução de Problemas'];

function withMockTaxonomies(block) {
  const names = [];
  if (block.taxonomy_name) names.push(block.taxonomy_name);
  if (block.taxonomy_name && block.position % 2 === 0) {
    names.push(MOCK_SECONDARY_TAXONOMIES[(block.position / 2) % MOCK_SECONDARY_TAXONOMIES.length]);
  }
  return { ...block, taxonomy_names: names };
}

const TABLE_HEAD = [
  { label: 'Aluno' },
  { label: 'Status', width: 150 },
  { label: '% concluído', width: 110 },
  { label: '% dominado', width: 110 },
  { label: 'Início', width: 160 },
  { label: 'Último acesso', width: 160 },
  { label: 'Ações', width: 420 }
];

const EMPTY_FILTERS = { regionalId: '', institutionId: '', courseId: '', classroomId: '' };

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

export default function TrilhaStudents() {
  const { learningPathId } = useParams();
  const [rows, setRows] = useState([]);
  const [trilhaName, setTrilhaName] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [performanceTarget, setPerformanceTarget] = useState(null);
  const { session } = useSession();
  const teacherOnly = isTeacherOnly(session);
  const playfulTeacher = usePlayfulTeacher();
  const client = useClient();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [regionals, setRegionals] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = (page = 1) => {
    setLoading(true);
    return adminLearningPathsApi
      .students(learningPathId, { q: debouncedSearch, page, perPage: 25, ...filters })
      .then(json => {
        const payload = json?.data || [];
        setRows(payload.map(d => ({ id: d.id, ...d.attributes })));
        const meta = json?.meta || {};
        setTrilhaName(meta.learning_path?.name || '');
        setPagination({
          currentPage: meta.current_page || 1,
          totalPages: meta.total_pages || 1,
          totalCount: meta.total_count || payload.length
        });
      })
      .catch(() => {
        toast.error('Erro ao carregar alunos da trilha');
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId, debouncedSearch, filters]);

  // Opções dos selects: carregadas uma vez e independentes entre si (escolher um filtro não muda as opções dos outros).
  useEffect(() => {
    client.fetch('regionals').then(res => setRegionals(res?.data || []));
    client.fetch('institutions').then(res => setInstitutions(res?.data || []));
    client.fetch('courses/simple').then(res => setCourses(res?.data || []));
    client.fetch('classrooms/simple').then(res => setClassrooms(res?.data || []));
  }, [client]);

  const changeFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value ?? '' }));

  // Drill-down: carrega os blocos do aluno selecionado.
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    adminLearningPathsApi
      .studentDetail(selected.id)
      .then(json => {
        const attrs = json?.data?.attributes || null;
        setDetail(attrs && { ...attrs, blocks: (attrs.blocks || []).map(withMockTaxonomies) });
      })
      .catch(() => toast.error('Erro ao carregar progresso do aluno'))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  const breadcrumbs = {
    title: trilhaName || 'Alunos da trilha',
    nav: [
      { route: PATHS.TRILHAS_ADMIN.LIST, name: teacherOnly ? 'Trilhas' : 'Gerenciar trilhas' },
      { name: 'Alunos', isActive: true }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Playful (professor): banner no padrão da lista de Desafio ao Vivo. */}
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={PENSANDO}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={150}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text="Acompanhe o progresso da trilha e desempenho por matéria dos seus alunos! 📈"
          />
        </div>
      )}

      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="student-search">Buscar aluno</Label>
            <Input
              id="student-search"
              type="search"
              placeholder="Nome ou e-mail"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
          <FilterSelect
            label="GRE"
            blankLabel="Todas"
            searchable
            value={filters.regionalId}
            onValueChange={v => changeFilter('regionalId', v)}
            options={regionals}
          />
          <FilterSelect
            label="Instituição"
            blankLabel="Todas"
            searchable
            value={filters.institutionId}
            onValueChange={v => changeFilter('institutionId', v)}
            options={institutions}
          />
          <FilterSelect
            label="Curso"
            blankLabel="Todos"
            searchable
            value={filters.courseId}
            onValueChange={v => changeFilter('courseId', v)}
            options={courses}
          />
          <FilterSelect
            label="Turma"
            blankLabel="Todas"
            searchable
            value={filters.classroomId}
            onValueChange={v => changeFilter('classroomId', v)}
            options={classrooms}
          />
        </FilterContent>
      </Filter>

      {loading ? (
        <Loader />
      ) : (
        <>
          <p className="mb-2 text-sm text-neutral">{pagination.totalCount} aluno(s) iniciaram esta trilha.</p>
          {rows.length === 0 ? (
            <EmptyState
              type="data"
              variant="plain"
            />
          ) : (
            <Table className="min-w-[1320px]">
              <TableHeaderWrapper tableHead={TABLE_HEAD} />
              <TableBody>
                {rows.map(r => {
                  const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.active;
                  return (
                    <TableRow key={r.id}>
                      <TableCell>
                        {playfulTeacher ? (
                          <>
                            <p
                              className="mb-0 truncate text-sm font-bold"
                              style={{ color: '#222731' }}
                              title={r.user_name}
                            >
                              {r.user_name}
                            </p>
                            <p className="mb-0 mt-0.5 truncate text-sm text-neutral-400">{r.user_email}</p>
                          </>
                        ) : (
                          <>
                            <p className="mb-0 text-sm font-semibold text-primary-dark">{r.user_name}</p>
                            <p className="mb-0 text-sm text-neutral">{r.user_email}</p>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        {playfulTeacher ? (
                          <Badge
                            variant={PLAYFUL_STATUS_VARIANT[r.status] || PLAYFUL_STATUS_VARIANT.active}
                            size="sm"
                            className="rounded-full text-sm"
                          >
                            {cfg.label}
                          </Badge>
                        ) : (
                          <Badge
                            size="sm"
                            variant={cfg.variant}
                          >
                            {cfg.label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{Math.round(r.percent_walked ?? 0)}%</TableCell>
                      <TableCell className="text-sm tabular-nums">{Math.round(r.percent_mastered ?? 0)}%</TableCell>
                      <TableCell className="text-sm tabular-nums">{formatDate(r.generated_at)}</TableCell>
                      <TableCell className="text-sm tabular-nums">{formatDate(r.last_accessed_at)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {playfulTeacher ? (
                          <div className="flex flex-nowrap items-center justify-end gap-2">
                            <PlayfulCardButton
                              variant="outline"
                              icon={FiActivity}
                              className="h-7 w-auto px-4 text-sm"
                              onClick={() => setSelected(r)}
                            >
                              Progresso na Trilha
                            </PlayfulCardButton>
                            <PlayfulCardButton
                              icon={FiBarChart2}
                              className="-mt-[3px] h-7 w-auto px-4 text-sm"
                              onClick={() => setPerformanceTarget(r)}
                            >
                              Desempenho na Trilha
                            </PlayfulCardButton>
                          </div>
                        ) : (
                          <div className="flex flex-nowrap items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              onClick={() => setSelected(r)}
                            >
                              <ButtonIcon>
                                <FiActivity />
                              </ButtonIcon>{' '}
                              Progresso na Trilha
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              onClick={() => setPerformanceTarget(r)}
                            >
                              <ButtonIcon>
                                <FiBarChart2 />
                              </ButtonIcon>{' '}
                              Desempenho na Trilha
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p =>
                playfulTeacher ? (
                  <PlayfulCardButton
                    key={p}
                    variant={p === pagination.currentPage ? 'continue' : 'outline'}
                    className="h-8 w-8 flex-none px-0 text-sm"
                    onClick={() => load(p)}
                  >
                    {p}
                  </PlayfulCardButton>
                ) : (
                  <Button
                    key={p}
                    size="xs"
                    variant={p === pagination.currentPage ? 'default' : 'outline'}
                    onClick={() => load(p)}
                  >
                    {p}
                  </Button>
                )
              )}
            </div>
          )}
        </>
      )}

      <Dialog
        open={Boolean(selected)}
        onOpenChange={isOpen => {
          if (!isOpen) setSelected(null);
        }}
      >
        <DialogContent
          size="lg"
          className="overflow-x-hidden"
        >
          <DialogHeader>
            <DialogTitle>{selected?.user_name}</DialogTitle>
            <DialogDescription>
              Progresso na trilha {detail?.learning_path_name || trilhaName || ''} — {Math.round(selected?.percent_walked ?? 0)}% concluído, {Math.round(selected?.percent_mastered ?? 0)}% dominado.
            </DialogDescription>
          </DialogHeader>
          <div className="min-w-0">
            {detailLoading ? (
              <p className="py-4 text-center text-sm text-neutral">Carregando blocos...</p>
            ) : (
              <ul className="space-y-2">
                {(detail?.blocks || []).map(b => {
                  const state = BLOCK_STATE[b.state] || BLOCK_STATE.pending;
                  return (
                    <li
                      key={b.position}
                      className="flex items-start gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
                    >
                      <span className="w-8 shrink-0 text-center font-mono text-xs text-neutral">{b.position}</span>
                      <div className="min-w-0 flex-1 basis-40">
                        <div className="flex items-center justify-between gap-2">
                          <p className="mb-0 min-w-0 truncate text-sm font-medium text-primary-dark">{b.video_title || b.taxonomy_name || BLOCK_KIND[b.kind]}</p>
                          <Badge
                            variant={state.variant}
                            className="shrink-0"
                          >
                            {state.label}
                          </Badge>
                        </div>
                        <p className="mb-0 text-xs text-neutral">
                          {BLOCK_KIND[b.kind] || b.kind}
                          {b.completed_at ? ` · concluído em ${formatDate(b.completed_at)}` : ''}
                        </p>
                        {b.taxonomy_names?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {b.taxonomy_names.map(name => (
                              <Badge
                                key={name}
                                variant="muted"
                                size="xs"
                                className="font-bold text-neutral-dark"
                              >
                                {name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
                {(detail?.blocks || []).length === 0 && <p className="py-4 text-center text-sm text-neutral">Sem blocos para exibir.</p>}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TrilhaPerformanceModal
        student={performanceTarget}
        open={Boolean(performanceTarget)}
        onOpenChange={isOpen => {
          if (!isOpen) setPerformanceTarget(null);
        }}
      />
    </DashboardContent>
  );
}
